import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@wulftime/core';
import { useToast } from '../components/ui';
import { api, ApiError, getAccessToken, onSessionReplaced, refreshSession, setAccessToken, tokenIdentity } from './api';

export interface Me {
  user: { id: string; email: string; fullName: string; isSuperadmin: boolean; totpEnabled: boolean };
  sessionId: string | null;
  companies: { id: string; name: string; status: string }[];
  company: null | {
    id: string;
    name: string;
    timezone: string;
    logoVersion: string | null;
    status: string;
    plan: { code: string | null; name: string | null; status: string | null; features: string[]; limits: Record<string, number | null> };
    onboarding: { step: number; completed: boolean } | null;
    billing?: { paymentMethodMissing: boolean; trialEndsAt: string | null };
    require2fa: string;
    roles: { code: string; name: string; scopeType: string; scopeId: string | null }[];
    permissions: Permission[];
    employee: null | { id: string; code: string; firstName: string; lastName: string; gpsNoticeAcknowledgedAt: string | null };
  };
  mfaSetupRequired: boolean;
  supportAccess: { id: string } | null;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ mfaRequired?: boolean; challengeToken?: string }>;
  verifyMfa: (challengeToken: string, code: string, rememberDevice?: boolean) => Promise<void>;
  acceptTokens: (t: { accessToken: string }) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  can: (p: Permission | Permission[]) => boolean;
  hasFeature: (f: string) => boolean;
  isAdminArea: boolean;
}

const Ctx = createContext<AuthState | null>(null);

function broadcastIdentity(identity: string | null) {
  try {
    const ch = new BroadcastChannel('wulftime-auth');
    ch.postMessage({ identity });
    ch.close();
  } catch {
    /* sin BroadcastChannel */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();
  const toast = useToast();

  const reload = useCallback(async () => {
    try {
      setMe(await api.get<Me>('/api/v1/auth/me'));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null);
      else throw e;
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (await refreshSession()) await reload().catch(() => setMe(null));
      setLoading(false);
    })();
  }, [reload]);

  // La sesión (permisos, plan, empresa, logo, estado de la suscripción) se mantiene al día: tras cada guardado, al volver
  // a la pestaña y cada minuto. Así los cambios hechos por otra persona (p. ej. un rol nuevo) llegan sin recargar.
  useEffect(() => {
    if (!me) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') reload().catch(() => {});
    };
    const t = window.setInterval(refresh, 60_000);
    window.addEventListener('wulftime:data-changed', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('wulftime:data-changed', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [Boolean(me), reload]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aviso tras recargar por un cambio de cuenta en otra pestaña
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('wt-auth-notice');
      if (msg) {
        sessionStorage.removeItem('wt-auth-notice');
        toast.info(msg);
      }
    } catch {
      /* sin almacenamiento */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pestañas sincronizadas: al entrar, salir o cambiar de empresa en una pestaña, las demás se actualizan.
  useEffect(() => {
    const goHome = (msg?: string) => {
      if (msg) {
        try {
          sessionStorage.setItem('wt-auth-notice', msg);
        } catch {
          /* sin almacenamiento: se recarga igualmente */
        }
      }
      window.location.assign('/');
    };
    const offReplaced = onSessionReplaced(() => goHome('Se ha iniciado sesión con otra cuenta en otra pestaña de este navegador. Se muestra la sesión actual.'));
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel('wulftime-auth');
      ch.onmessage = (e: MessageEvent<{ identity: string | null }>) => {
        if (e.data?.identity !== tokenIdentity(getAccessToken())) goHome(e.data?.identity ? 'Se ha iniciado sesión con otra cuenta en otra pestaña de este navegador.' : undefined);
      };
    } catch {
      /* navegador sin BroadcastChannel: queda la detección al renovar el token */
    }
    return () => {
      offReplaced();
      ch?.close();
    };
  }, []);

  const acceptTokens = useCallback(
    async (t: { accessToken: string }) => {
      setAccessToken(t.accessToken);
      qc.clear();
      await reload();
      broadcastIdentity(tokenIdentity(t.accessToken));
    },
    [qc, reload],
  );

  const value = useMemo<AuthState>(() => {
    const perms = new Set(me?.company?.permissions ?? []);
    const can = (p: Permission | Permission[]) => (Array.isArray(p) ? p : [p]).some((x) => perms.has(x));
    const adminPerms: Permission[] = ['employees.view', 'time_records.view', 'company.manage', 'users.manage', 'reports.view', 'audit.view', 'incidents.view', 'policies.manage'];
    return {
      me,
      loading,
      login: async (email, password) => {
        const r = await api.post<{ accessToken?: string; mfaRequired?: boolean; challengeToken?: string }>('/api/v1/auth/login', { email, password, deviceName: navigator.userAgent.slice(0, 90) });
        if (r.mfaRequired) return { mfaRequired: true, challengeToken: r.challengeToken };
        await acceptTokens({ accessToken: r.accessToken! });
        return {};
      },
      verifyMfa: async (challengeToken, code, rememberDevice = false) => {
        const r = await api.post<{ accessToken: string }>('/api/v1/auth/2fa/verify', { challengeToken, code, rememberDevice });
        await acceptTokens(r);
      },
      acceptTokens,
      logout: async () => {
        await api.post('/api/v1/auth/logout').catch(() => {});
        setAccessToken(null);
        setMe(null);
        qc.clear();
        broadcastIdentity(null);
      },
      reload,
      switchCompany: async (companyId) => {
        const r = await api.post<{ accessToken: string }>('/api/v1/auth/switch-company', { companyId });
        await acceptTokens(r);
      },
      can,
      hasFeature: (f) => Boolean(me?.company?.plan.features.includes(f)),
      isAdminArea: can(adminPerms),
    };
  }, [me, loading, acceptTokens, reload, qc]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fuera de AuthProvider');
  return v;
}
